import type { Mesh, MeshStandardMaterial } from 'three';

import type { AddPanelInfo } from '../../../model/add-panel-info';
import type { GeometryBuilderConfig, GeometryResult } from './types';
import { createPanelCfn } from './functions/create-panel.cfn';
import { createFloorCfn } from './functions/create-floor.cfn';

/**
 * GeometryBuilder creates 3D geometry from panel info.
 *
 * Features:
 * - Create extruded geometry from vertices
 * - Apply materials based on texture keys
 * - Handle CSG operations for slat textures
 * - Quality-based optimizations
 *
 * @example
 * ```typescript
 * const builder = new GeometryBuilder({
 *   basePath: '/assets/',
 *   renderQuality: 7,
 *   qualitySettings: null,
 *   materials: materialsMap
 * });
 *
 * const result = await builder.createPanel(panelInfo);
 * islandGroup.add(result.mesh);
 * ```
 */
export class GeometryBuilder {
	#config: GeometryBuilderConfig;
	#gltfModelCache = new Map<string, Mesh>();

	constructor(config: GeometryBuilderConfig) {
		this.#config = config;
	}

	// ========== CFN Accessors ==========

	/** @internal Access to config for CFN functions */
	get configAccess(): GeometryBuilderConfig {
		return this.#config;
	}

	/** @internal Access to GLTF model cache for CFN functions */
	get gltfModelCache(): Map<string, Mesh> {
		return this.#gltfModelCache;
	}

	// ========== Public API ==========

	/**
	 * Update configuration
	 */
	updateConfig(config: Partial<GeometryBuilderConfig>): void {
		this.#config = { ...this.#config, ...config };
	}

	/**
	 * Update materials map
	 */
	setMaterials(materials: Map<string, MeshStandardMaterial>): void {
		this.#config.materials = materials;
	}

	/**
	 * Create a panel from AddPanelInfo
	 */
	async createPanel(api: AddPanelInfo): Promise<GeometryResult> {
		return createPanelCfn(this, { api });
	}

	/**
	 * Create a floor/carpet mesh
	 */
	createFloor(material: MeshStandardMaterial, size: number = 1000): Mesh {
		return createFloorCfn(this, { material, size });
	}

	/**
	 * Dispose of cached resources
	 */
	dispose(): void {
		this.#gltfModelCache.clear();
	}
}
